import api from './api';

export type PlanPaymentMethod = 'TON' | 'STARS';

export interface PublicPricing {
  priceUsd: number;
  durationDays: number;
  feeBpsWithoutPlan: number;
  stars: { amount: number };
  ton: { enabled: boolean; amount: number | null };
}

export interface BotView {
  botId: string;
  username: string;
  name: string;
  link: string;
  connectedAt: string | null;
}

export interface PlanOverview {
  plan: { tier: 'FREE' | 'PRO'; active: boolean; expiresAt: string | null; daysLeft: number };
  pricing: PublicPricing;
  bot: BotView | null;
}

export interface BotHealth {
  reachable: boolean;
  webhookOk: boolean;
  pendingUpdates: number;
  lastError: string | null;
  lastErrorAt: string | null;
}

export interface PlanOrder {
  orderId: string;
  method: PlanPaymentMethod;
  status: 'PENDING' | 'PAID' | 'EXPIRED';
  priceUsd: number;
  durationDays: number;
  paidAt: string | null;
  ton?: {
    address: string;
    memo: string;
    amount: number;
    amountNano: string;
    usdPerTon: number;
    expiresAt: string | null;
    underpaidNano: string | null;
    deepLink: string;
    tonkeeperLink: string;
  };
  stars?: { amount: number; invoiceLink: string };
  plan?: { active: boolean; expiresAt: string | null };
}

export const plansApi = {
  getPricing: async (): Promise<PublicPricing> => (await api.get('/plans/pricing')).data,
  getMe: async (): Promise<PlanOverview> => (await api.get('/plans/me')).data,
  checkout: async (method: PlanPaymentMethod): Promise<PlanOrder> =>
    (await api.post('/plans/pro/checkout', { method })).data,
  getOrder: async (orderId: string): Promise<PlanOrder> => (await api.get(`/plans/orders/${orderId}`)).data,
  getBot: async (): Promise<{ bot: BotView | null; health: BotHealth | null }> => (await api.get('/plans/bot')).data,
  connectBot: async (token: string): Promise<{ bot: BotView }> => (await api.post('/plans/bot', { token })).data,
  repairBot: async (): Promise<{ bot: BotView }> => (await api.post('/plans/bot/repair')).data,
  disconnectBot: async (): Promise<{ disconnected: boolean }> => (await api.delete('/plans/bot')).data,
};

/** Message d'erreur lisible renvoyé par l'API (NestJS) ou, à défaut, le message réseau. */
export function apiErrorMessage(error: any, fallback: string): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || error?.message || fallback;
}
